import * as Papa from "papaparse";
import { styled } from "goober";
import { useEffect, useMemo, useState } from "react";
import { MAX_DOCUMENT_SIZE } from "../../shared/attachmentPolicy";
import { base64ToUint8Array } from "../lib/base64";
import { Button } from "./Button";
import { Icon } from "./Icon";
import { Text } from "./Text";
import { WavyLine } from "./WavyLine";

const CSV_PAGE_SIZE = 50;

type CsvData = {
  columns: string[];
  rows: string[][];
};
type CsvState =
  | { status: "idle" | "loading" | "too-large" | "missing-source" }
  | { status: "error"; message: string }
  | { status: "ready"; data: CsvData };

const Root = styled("div")`
  width: 100%;
  height: 100%;
  min-height: 0;
  display: flex;
  flex-direction: column;
`;

const Toolbar = styled("div")`
  display: flex;
  align-items: center;
  gap: 10px;
  min-height: 52px;
  padding: 10px 12px;
  border-bottom: 0.5px solid var(--border-alpha);

  @media (max-width: 720px) {
    flex-wrap: wrap;
  }
`;

const Title = styled("div")`
  display: flex;
  flex-direction: column;
  gap: 2px;
  min-width: 0;
  flex: 1;
`;

const SearchWrap = styled("label")`
  display: flex;
  align-items: center;
  gap: 8px;
  width: min(300px, 42%);
  min-width: 180px;
  height: 32px;
  padding: 0 10px;
  border-radius: 8px;
  outline: 0.5px solid var(--border-alpha);
  color: var(--text-secondary);
  background: var(--surface-primary);

  &:has(input:focus) {
    outline-color: var(--control-bg);
    box-shadow: 0 0 0 3px var(--border-alpha);
  }

  @media (max-width: 720px) {
    width: 100%;
  }
`;

const SearchInput = styled("input")`
  min-width: 0;
  flex: 1;
  border: 0;
  outline: 0;
  background: transparent;
  color: var(--text-primary);
  font: inherit;
  font-size: 12px;
  line-height: 16px;

  &::placeholder {
    opacity: 0.35;
  }
`;

const Columns = styled("div")`
  display: flex;
  align-items: center;
  gap: 5px;
  padding: 7px 12px;
  border-bottom: 0.5px solid var(--border-alpha);
  overflow-x: auto;
  min-height: 34px;
`;

const ColumnPill = styled("span")`
  display: inline-flex;
  align-items: center;
  min-width: 0;
  flex: 0 0 auto;
  max-width: 180px;
  border-radius: 6px;
  background: var(--alpha4);
  color: var(--text-secondary);
  padding: 3px 7px;
  font-size: 11px;
  line-height: 16px;
  white-space: nowrap;
  overflow: hidden;
  text-overflow: ellipsis;
`;

const ResultHeader = styled("div")`
  display: flex;
  align-items: center;
  justify-content: space-between;
  gap: 8px;
  min-height: 32px;
  padding: 7px 12px;
  border-bottom: 0.5px solid var(--border-alpha);
`;

const ResultViewport = styled("div")`
  flex: 1;
  min-height: 0;
  overflow: auto;
`;

const DataTable = styled("table")`
  width: 100%;
  border-collapse: collapse;
  font-size: 11px;
  line-height: 16px;
  color: var(--text-primary);
`;

const HeaderCell = styled("th")`
  position: sticky;
  top: 0;
  z-index: 1;
  max-width: 320px;
  height: 32px;
  padding: 0 12px;
  text-align: left;
  font-weight: 500;
  color: var(--text-secondary);
  background: var(--surface-primary);
  box-shadow: 0 0.5px 0 var(--border-alpha);
  white-space: nowrap;
  overflow: hidden;
  text-overflow: ellipsis;
`;

const Cell = styled("td")`
  max-width: 320px;
  padding: 7px 12px;
  border-top: 0.5px solid var(--border-alpha);
  white-space: nowrap;
  overflow: hidden;
  text-overflow: ellipsis;
`;

const Footer = styled("div")`
  display: flex;
  align-items: center;
  justify-content: space-between;
  gap: 8px;
  min-height: 40px;
  padding: 8px 12px;
  border-top: 0.5px solid var(--border-alpha);
`;

const Pager = styled("div")`
  display: flex;
  align-items: center;
  gap: 6px;
`;

const Center = styled("div")`
  width: 100%;
  height: 100%;
  display: flex;
  align-items: center;
  justify-content: center;
  flex-direction: column;
  gap: 8px;
  color: var(--text-secondary);
`;

function formatBytes(bytes: number) {
  if (bytes < 1024) return `${bytes} B`;
  if (bytes < 1024 * 1024) return `${(bytes / 1024).toFixed(1)} KB`;
  return `${(bytes / (1024 * 1024)).toFixed(1)} MB`;
}

function base64ToText(base64: string) {
  return new TextDecoder().decode(base64ToUint8Array(base64));
}

function csvCell(value: unknown) {
  if (value == null) return "";
  return String(value);
}

function csvColumns(row: string[]) {
  const seen = new Map<string, number>();
  return row.map((raw, i) => {
    const base = raw.trim() || `Column ${i + 1}`;
    const count = seen.get(base) ?? 0;
    seen.set(base, count + 1);
    return count === 0 ? base : `${base} ${count + 1}`;
  });
}

function parseCsv(text: string): CsvData {
  const parsed = Papa.parse<string[]>(text, {
    skipEmptyLines: true,
  });
  if (parsed.errors.length > 0) {
    throw new Error(parsed.errors[0]?.message ?? "Unable to parse CSV.");
  }

  const rows = parsed.data.map((row) => row.map(csvCell));
  const header = rows[0] ?? [];
  const columns = csvColumns(header);
  return {
    columns,
    rows: rows.slice(1),
  };
}

function openUrl(url: string) {
  return new URL(url, location.href).href;
}

function csvErrorMessage(err: unknown) {
  if (err instanceof Error) return err.message;
  return String(err);
}

function useCsvData({
  enabled,
  inlineData,
  sizeBytes,
  url,
}: {
  enabled: boolean;
  inlineData: string | null;
  sizeBytes: number;
  url: string | null;
}) {
  const [state, setState] = useState<CsvState>({ status: "idle" });

  useEffect(() => {
    if (!enabled) return;
    if (sizeBytes > MAX_DOCUMENT_SIZE) {
      setState({ status: "too-large" });
      return;
    }
    if (!inlineData && !url) {
      setState({ status: "missing-source" });
      return;
    }

    let cancelled = false;
    const controller = new AbortController();
    setState({ status: "loading" });

    const load = inlineData
      ? Promise.resolve(base64ToText(inlineData))
      : fetch(openUrl(url!), {
          credentials: "include",
          signal: controller.signal,
        }).then((res) => {
          if (!res.ok)
            throw new Error(`File read failed with HTTP ${res.status}.`);
          return res.text();
        });

    load
      .then((text) => parseCsv(text))
      .then((data) => {
        if (!cancelled) setState({ status: "ready", data });
      })
      .catch((err) => {
        if (cancelled || controller.signal.aborted) return;
        setState({ status: "error", message: csvErrorMessage(err) });
      });

    return () => {
      cancelled = true;
      controller.abort();
    };
  }, [enabled, inlineData, sizeBytes, url]);

  return state;
}

function Loading() {
  return (
    <Center>
      <WavyLine size={24} opacity={0.4} />
    </Center>
  );
}

function Status({ children }: { children: React.ReactNode }) {
  return (
    <Center>
      <Text variant="text-small" color="var(--text-secondary)">
        {children}
      </Text>
    </Center>
  );
}

export function CsvPreview({
  enabled,
  inlineData,
  sizeBytes,
  url,
}: {
  enabled: boolean;
  inlineData: string | null;
  sizeBytes: number;
  url: string | null;
}) {
  const state = useCsvData({ enabled, inlineData, sizeBytes, url });
  const [search, setSearch] = useState("");
  const [page, setPage] = useState(0);

  const data = state.status === "ready" ? state.data : null;
  const filteredRows = useMemo(() => {
    const trimmedSearch = search.trim().toLowerCase();
    if (!data || !trimmedSearch) return data?.rows ?? [];
    return data.rows.filter((row) =>
      row.some((cell) => cell.toLowerCase().includes(trimmedSearch))
    );
  }, [data, search]);

  useEffect(() => {
    setPage(0);
  }, [data, search]);

  if (state.status === "idle" || state.status === "loading") return <Loading />;
  if (state.status === "missing-source") {
    return <Status>CSV preview needs file data.</Status>;
  }
  if (state.status === "too-large") {
    return (
      <Status>
        CSV preview supports files up to {formatBytes(MAX_DOCUMENT_SIZE)}.
      </Status>
    );
  }
  if (state.status === "error") {
    return <Status>Unable to open CSV: {state.message}</Status>;
  }
  if (!data || data.columns.length === 0)
    return <Status>No columns found.</Status>;

  const offset = page * CSV_PAGE_SIZE;
  const rows = filteredRows.slice(offset, offset + CSV_PAGE_SIZE);
  const start = rows.length > 0 ? offset + 1 : 0;
  const end = offset + rows.length;
  const hasPrevious = page > 0;
  const hasNext = end < filteredRows.length;

  return (
    <Root>
      <Toolbar>
        <Title>
          <Text variant="text-regular-md">CSV</Text>
          <Text variant="text-small" color="var(--text-secondary)">
            {data.rows.length} rows
          </Text>
        </Title>
        <SearchWrap>
          <Icon name="search" size={12} />
          <SearchInput
            value={search}
            onChange={(e) => setSearch(e.target.value)}
            placeholder="Search table"
          />
        </SearchWrap>
      </Toolbar>
      <Columns>
        <Text
          variant="text-small-md"
          color="var(--text-primary)"
          style={{ flex: "0 0 auto" }}
        >
          Columns
        </Text>
        {data.columns.map((column) => (
          <ColumnPill key={column} title={column}>
            {column}
          </ColumnPill>
        ))}
      </Columns>
      <ResultHeader>
        <Text variant="text-small" color="var(--text-secondary)">
          {rows.length > 0 ? `${start}-${end} rows` : "No rows"}
        </Text>
        <Text variant="text-small" color="var(--text-secondary)">
          {data.columns.length} columns
        </Text>
      </ResultHeader>
      <ResultViewport>
        {rows.length === 0 ? (
          <Status>No rows found.</Status>
        ) : (
          <DataTable>
            <thead>
              <tr>
                {data.columns.map((column) => (
                  <HeaderCell key={column}>{column}</HeaderCell>
                ))}
              </tr>
            </thead>
            <tbody>
              {rows.map((row, rowIndex) => (
                <tr key={`${offset + rowIndex}:${row.join("|")}`}>
                  {data.columns.map((column) => (
                    <Cell key={column}>
                      {row[data.columns.indexOf(column)] ?? ""}
                    </Cell>
                  ))}
                </tr>
              ))}
            </tbody>
          </DataTable>
        )}
      </ResultViewport>
      <Footer>
        <Text variant="text-small" color="var(--text-secondary)">
          Page {page + 1}
        </Text>
        <Pager>
          <Button
            type="button"
            variant="secondary"
            icon="chevron-left"
            disabled={!hasPrevious}
            onClick={() => setPage((p) => Math.max(0, p - 1))}
          />
          <Button
            type="button"
            variant="secondary"
            icon="chevron-right"
            disabled={!hasNext}
            onClick={() => setPage((p) => p + 1)}
          />
        </Pager>
      </Footer>
    </Root>
  );
}
